import React, { Component } from 'react';
import { Route } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';
import Constants from '../../../definitions/constants';
import InterfaceTooling from '../../../tooling/interfaceTooling';
import JavascriptUtilsTooling from '../../../tooling/javascriptUtilsTooling';
import FormatTooling from '../../../tooling/formatTooling';
import EntityTableTooling from '../../../tooling/entityTableTooling';
import { intl } from '../../../locales/localesConfig';
import LocaleTooling from '../../../tooling/localeTooling';
import toastr from 'toastr';

/* Actions */
import * as metricDataAdminActions from '../../actions/metricDataAdmin/index';

/*Components*/
import FullScreenGlassLoading from '../../components/common/interface/fullScreenGlassLoading';
import GlassConfirmPopup from '../../components/common/interface/glassConfirmPopup';
import Spinner from '../../components/common/interface/spinner';
import Filters from '../../components/metricDataAdmin/filters';
import Header from '../../components/metricDataAdmin/header';
import Table from '../../components/metricDataAdmin/table';
import ImportMetricDataFormPopup from '../../components/metricDataAdmin/importMetricDataFormPopup';
import FormPopup from '../../components/metricDataAdmin/formPopup';
import LoadDataInfoPopup from '../../components/metricDataAdmin/loadDataInfoPopup';
import ErrorLoadingView from '../../components/common/interface/errorLoadingView';

const popupIds = {
  formPopup: "formPopup",
  deleteConfirmPopup: "deleteConfirmPopup",
  importFormPopup: "importFormPopup",
  deleteMetricsDataPopup: "deleteMetricsDataPopup",
  deleteMetricsMarksDataPopup: "deleteMetricsMarksDataPopup",
  loadDataInfoPopup: "loadDataInfoPopup",  
  closeFormConfirmPopup: "closeFormConfirmPopup",
}

const initialState = {
  dataFilters: {
    metricsFilter: [],
    calculationUnitsSelected: [],
    periodIdSelected: '',
    showMarks: false,
    hide_inactive_calculation_units: false,
  },
  dataFiltersActive: {},
  filtersText: "",
  selectedPeriodName: "",
  metricGroupImport: Constants.metricGroups.CUSTOM_METRICS,
  selectedImportFile: null,  
  otherFilters: [],
  refreshFilter: false,
  filtersCollapse: false,
  showAudits: false,
  filtersChanged: true,
  formHasChanges: false,
};

class MetricDataAdmin extends Component {
  constructor(props) {
    super(props);
    this.state = JavascriptUtilsTooling.deepCopy(initialState);
  }
 
  async componentDidMount() {
    await this.props.actions.getPeriodsList();
    await this.props.actions.getMetricsList();
    await this.props.actions.getMetricsGroupsWithDefaults();
    await this.props.actions.getImportFilesList();
  }
  
  componentWillUnmount() {
    this.props.actions.reset();
  }
  
  componentDidUpdate(prevProps) {
    //handle download imoprt feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.downloadImport.status],
      [this.props.downloadImport.status],
      intl.formatMessage({ id: "metricDataAdmin.downloadImportFileSuccess" }),
      intl.formatMessage({ id: "common.entity.downloadImportFileFailure" })
    );
    
    //handle export metrics data feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.exportMetricsData.status],
      [this.props.exportMetricsData.status],
      intl.formatMessage({ id: "metricDataAdmin.exportMetricsDataSuccess" }),
      intl.formatMessage({ id: "metricDataAdmin.exportMetricsDataFailure" })
    );

    //handle imoprt feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.import.status],
      [this.props.import.status],
      intl.formatMessage(
        { id: "common.entity.uploadImportSuccess" },
        {entityNameSingular: intl.formatMessage({ id: "metricDataAdmin.entityNamePlural" })}),
      null,
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.importFormPopup])) {
          InterfaceTooling.popupHide(popupIds.importFormPopup, this.setState.bind(this));
        }    
      }]  
    );  
    
    //handle delete metrics data feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.deleteMetricsData.status],
      [this.props.deleteMetricsData.status],
      intl.formatMessage({ id: "metricDataAdmin.deleteMetricsDataSuccess" }),
      this.props.deleteMetricsData.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "metricDataAdmin.deleteMetricsDataError" }),
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.deleteMetricsDataPopup])) {
          InterfaceTooling.popupHide(popupIds.deleteMetricsDataPopup, this.setState.bind(this));
        }
        this.handleReloadDataWithNewFilters();
      }]    
    );

    //handle delete metrics marks feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.deleteMetricsMarksData.status],
      [this.props.deleteMetricsMarksData.status],
      intl.formatMessage({ id: "metricDataAdmin.deleteMetricsMarksDataSuccess" }),
      this.props.deleteMetricsMarksData.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "metricDataAdmin.deleteMetricsMarksDataError" }),
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.deleteMetricsMarksDataPopup])) {
          InterfaceTooling.popupHide(popupIds.deleteMetricsMarksDataPopup, this.setState.bind(this));
        }
        this.handleReloadDataWithNewFilters();
      }]    
    );
    
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.save.status],
      [this.props.save.status],
      intl.formatMessage({id: "metricDataAdmin.saveSuccess"}),
      null,
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.formPopup])) {
          InterfaceTooling.popupHide(popupIds.formPopup, this.setState.bind(this));
        }
        this.handleReloadDataWithNewFilters();      
        this.setState({ formHasChanges: false });
      }]
    );

    //handle delete feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.delete.status],
      [this.props.delete.status],
      intl.formatMessage({ id: "metricDataAdmin.deleteSuccess" }),
      this.props.delete.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "metricDataAdmin.deleteError" }),
      [() => {
        if(InterfaceTooling.popupHasToRender(this.state[popupIds.deleteConfirmPopup])) {
          InterfaceTooling.popupHide(popupIds.deleteConfirmPopup, this.setState.bind(this));
        }
        this.handleReloadDataWithNewFilters();    
      }]    
    );
    
    //handle refresh virtual metrics feedback
    InterfaceTooling.apiCallResultFeedback(
      [prevProps.refreshVirtualMetricData.status],
      [this.props.refreshVirtualMetricData.status],
      intl.formatMessage( {id: "periodAdmin.refreshVirtualMetricDataCreateJobSuccess"}),
      this.props.refreshVirtualMetricData.status==423 ?
        intl.formatMessage({ id: "common.errors.systemLockedError" }) :
        intl.formatMessage({ id: "periodAdmin.refreshVirtualMetricDataCreateJobFailure" })
    );
    
    if (this.props.list.status===200 && prevProps.list.status !==200 && this.state.refreshFilter) {
      let otherFilters = [];
      this.props.list.additionalData.headers.forEach(item => { otherFilters.push("") });
      this.setState({
        otherFilters: otherFilters,
        refreshFilter: false
      });        
    }
    
    if (this.props.periodsList.status===200 && prevProps.periodsList.status !==200) {
      if(this.props.periodsList.data.length > 0) {
        let periodIdSelected = this.props.periodsList.data[0].id;
        let selectedPeriod = null;
        if(this.props.fixedPeriodId!="") {
          selectedPeriod = this.props.periodsList.data.find(item => item.id==this.props.fixedPeriodId);
          periodIdSelected = selectedPeriod ? selectedPeriod.id : null;
        }
        this.state.dataFilters.periodIdSelected = periodIdSelected;
        this.state.selectedPeriodName = selectedPeriod ? selectedPeriod.name : "";
        this.props.actions.setPeriodId(periodIdSelected);
        this.props.actions.getGroupsList(periodIdSelected);
      }
    }
  }
  
  handleOnChangePeriod = (event) => {
    let value = event.target.value;
    let newState = JavascriptUtilsTooling.deepCopy(initialState);
    let selectedPeriod = this.props.periodsList.data.find(item => item.id==value);
    newState.dataFilters.periodIdSelected = value;
    newState.selectedPeriodName = selectedPeriod ? selectedPeriod.name : "";
    this.setState(newState);
    this.props.actions.softReset();
    this.props.actions.setPeriodId(newState.dataFilters.periodIdSelected);
    this.props.actions.getGroupsList(newState.dataFilters.periodIdSelected);
  };

  handleOnChangeMetricGroup = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    var metricsFilter = this.state.dataFilters.metricsFilter;
    if(value!=Constants.metricGroups.CUSTOM_METRICS) {
      var metricGroupData = JSON.parse(value);
      var metric_ids = metricGroupData.metric_ids ? metricGroupData.metric_ids.split(",") : [];
      metricsFilter = [];
      metric_ids.forEach( (metric_id) => {
        var metric_found = this.props.metricsList.data.find( item => item.id == metric_id);
        if(metric_found) {
          metricsFilter.push(metric_found)
        }
      });
    }
    else {
      metricsFilter = [];
    }
    let dataFiltersNew = JavascriptUtilsTooling.deepCopy(this.state.dataFilters);
    dataFiltersNew.metricsFilter = metricsFilter;
    
    this.setState({
      metricGroupImport: value,
      dataFilters: dataFiltersNew,
      filtersChanged: true,
    });
  };

  handleOnChangeDataFilters = (event) => {
    let path = event.currentTarget.dataset.path;
    let value = this.state.dataFilters[path];
    let dataFiltersNew = JavascriptUtilsTooling.deepCopy(this.state.dataFilters);
    
    if(event.currentTarget.dataset.selectlist) {
      let index = event.currentTarget.dataset.index;
      let listValue = event.currentTarget.value;
      let isDelete = event.currentTarget.dataset.delete;
      
      let actual_selecteds = dataFiltersNew[path]
            
      if(isDelete) { 
        delete actual_selecteds[index];
        actual_selecteds = actual_selecteds.filter(item => item);
      } else {
        if(path=="metricsFilter") {
          var metric_finded = this.props.metricsList.data.find(item => item.id==listValue)
          actual_selecteds.push({id: listValue, name: metric_finded.name, dimension_string: metric_finded.dimension_string, metric_type: metric_finded.metric_type });         
        }
        else {
          var selectedItem = this.props.calculationUnitsFilter.data.find(item => item.id==listValue);
          var exists = this.state.dataFilters.calculationUnitsSelected.find(item => item.id==listValue);
          if(selectedItem && !exists) {
            actual_selecteds.push({id: listValue, name: selectedItem.name });
          }
          else if(exists) {
            toastr.options = Constants.interface.tostrOptions;
            toastr.warning(LocaleTooling.getCalculationUnitContextLocale(intl.formatMessage( {id: "calculationsHistory.calculationUnitAlreadyAdded"})),""); 
          }
        }
      }
      value = actual_selecteds;
    }
    else {
      value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    }
    dataFiltersNew[path] = value;

    this.setState({
      dataFilters: dataFiltersNew,
      filtersChanged: true,
    });    
  };

  handleLoadMetricsData = async (event) => {
    let dataFiltersActive = JavascriptUtilsTooling.deepCopy(this.state.dataFilters);
    this.setState({
      dataFiltersActive: dataFiltersActive,
      filtersText: this.getFiltersLegend(),
      refreshFilter: true,
      filtersChanged: false,
      showAudits: false
    })
    await this.props.actions.getList({
      metrics_ids: dataFiltersActive.metricsFilter.map( item => item.id ),
      calculation_unit_ids: dataFiltersActive.calculationUnitsSelected.map( item => item.id ),
      period_id: dataFiltersActive.periodIdSelected,
      page: 1,
      per_page: Constants.interface.metricDataListPageSize,
      show_marks: dataFiltersActive.showMarks,
      hide_inactive_calculation_units: dataFiltersActive.hide_inactive_calculation_units,
      timezone_offset: new Date().getTimezoneOffset()
    });
  };

  getFiltersLegend = () => {
    let filterItemsText = [];
    let calculationUnitsSelected = this.state.dataFilters.calculationUnitsSelected;
    filterItemsText.push(calculationUnitsSelected.length>0 ? LocaleTooling.getCalculationUnitContextLocale(intl.formatMessage({id: "metricDataAdmin.findByCalculationUnits"})) + ": " + 
      calculationUnitsSelected.map(item => item.name).join("; ") : "");
    filterItemsText = filterItemsText.filter(item => item!="").join(" | ");
    return filterItemsText;
  };

  fetchMetricsDataNextPage = async (event) => {
    var page = +event.currentTarget.dataset.targetpage;
    if(this.props.list.status === 200 && this.props.list.page<=this.props.list.totalPages) {
      this.handleReloadDataWithNewFilters(page, true);
    }  
  };

  handleShowImportFormPopup = (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.importFormPopup, this.state, 
      this.setState.bind(this), this.props.import.loading, () => {
        this.props.actions.resetImport();
        this.setState({
          selectedImportFile: null
        });
      }    
    );
  };

  handleDownloadImportSheet = async () => {
    let group_name = this.state.metricGroupImport!=Constants.metricGroups.CUSTOM_METRICS ? 
      JSON.parse(this.state.metricGroupImport).name : 
      "";
    await this.props.actions.downloadImportFile({
      group_name: group_name,
      metrics_ids: this.state.dataFilters.metricsFilter.map(item => item.id),
      period_id: this.state.dataFilters.periodIdSelected
    });
  };

  handleOnSaveImportForm = async (event) => {
    let files = [];
    if(!this.state.selectedImportFile) {
      files = document.getElementById('import_file').files;
      files = Object.keys(files).map(key => files[key]);
    }
    document.getElementById('import_file').value = '';
    let group_name = this.state.metricGroupImport!=Constants.metricGroups.CUSTOM_METRICS ? 
      JSON.parse(this.state.metricGroupImport).name : 
      "";
    await this.props.actions.importEntities(files,{
      metrics_ids: this.state.dataFilters.metricsFilter.map(item => item.id),
      group_name: group_name,
      import_file_url: this.state.selectedImportFile,
      period_id: this.state.dataFilters.periodIdSelected
    });
  };

  handleOnChangeImportFile = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    this.setState({
      selectedImportFile: value
    });
  };

  handleFiltersCollapse = (event) => {
    this.setState({
      filtersCollapse: !this.state.filtersCollapse
    });
  };

  handleFetchFilterCalculationUnits = async (keyword) => {
    await this.props.actions.getCalculationUnitsList(keyword, this.state.dataFilters.periodIdSelected);
  };

  handleDeleteMetricsDataPopup = (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.deleteMetricsDataPopup, this.state, 
      this.setState.bind(this), this.props.deleteMetricsData.loading
    );
  };

  handleDeleteMetricsDataConfirm = () => {
    this.props.actions.deleteMetricsData({
      metrics_ids: this.state.dataFiltersActive.metricsFilter.map( item => item.id ),
      calculation_unit_ids: this.state.dataFiltersActive.calculationUnitsSelected.map( item => item.id ),
      other_filters: this.state.otherFilters,
      other_filters_meta: this.props.list.additionalData.info,
      period_id: this.state.dataFilters.periodIdSelected
    });
  };

  handleDeleteMetricsMarksDataPopup = (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.deleteMetricsMarksDataPopup, this.state, 
      this.setState.bind(this), this.props.deleteMetricsMarksData.loading
    );
  };

  handleDeleteMetricsMarksDataConfirm = () => {
    this.props.actions.deleteMetricsMarksData({
      metrics_ids: this.state.dataFiltersActive.metricsFilter.map( item => item.id ),
      calculation_unit_ids: this.state.dataFiltersActive.calculationUnitsSelected.map( item => item.id ),
      other_filters: this.state.otherFilters,
      other_filters_meta: this.props.list.additionalData.info,
      period_id: this.state.dataFilters.periodIdSelected
    });
  };

  handleExportMetricsData = async () => {
    await this.props.actions.exportMetricsData({
      metrics_ids: this.state.dataFiltersActive.metricsFilter.map( item => item.id ),
      calculation_unit_ids: this.state.dataFiltersActive.calculationUnitsSelected.map( item => item.id ),
      other_filters: this.state.otherFilters,
      show_marks: this.state.dataFiltersActive.showMarks,
      hide_inactive_calculation_units: this.state.dataFiltersActive.hide_inactive_calculation_units,
      period_id: this.state.dataFilters.periodIdSelected
    });
  };

  handleOnChangeOtherFilters = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    let index = event.currentTarget.dataset.path;
    let selectlist = event.currentTarget.dataset.selectlist;
    let newOtherFilters = JavascriptUtilsTooling.deepCopy(this.state.otherFilters);
    newOtherFilters[index] = value
    //to ensure that in the action call the state is changed
    this.state.otherFilters = newOtherFilters;
    //to ensure reprint view
    this.setState({
      otherFilters: newOtherFilters
    });
    //reload onchange
    this.handleReloadDataWithNewFilters();
  };

  handleReloadDataWithNewFilters = (page = 1, pagging = false) => {
    this.props.actions.getList({
      page: page,
      per_page: Constants.interface.metricDataListPageSize,
      metrics_ids: this.state.dataFiltersActive.metricsFilter.map( item => item.id ),
      calculation_unit_ids: this.state.dataFiltersActive.calculationUnitsSelected.map( item => item.id ),
      other_filters: this.state.otherFilters,
      period_id: this.state.dataFiltersActive.periodIdSelected,
      show_marks: this.state.dataFiltersActive.showMarks,
      hide_inactive_calculation_units: this.state.dataFiltersActive.hide_inactive_calculation_units,
      timezone_offset: new Date().getTimezoneOffset()
    }, pagging);
  };

  handleShowFormPopup = (event) => {
    InterfaceTooling.handleShowFormPopupWithConfirmOnExit(
      event, popupIds.formPopup, this.state, 
      this.setState.bind(this), this.props.save.loading, () => {
        let recordObject = {};        
        if(event.currentTarget.dataset.recorddata) {
          let parsedObject = JSON.parse(event.currentTarget.dataset.recorddata)
          recordObject["values"] = parsedObject.values;
          recordObject["ids"] = parsedObject.ids;
        }
        else {
          let emptyData = [];
          this.props.list.additionalData.headers.forEach(item => { emptyData.push("") });
          recordObject["values"] = emptyData;
        }
        recordObject["info"] = this.props.list.additionalData.info;
        recordObject["period_id"] = this.state.dataFiltersActive.periodIdSelected;
        this.props.actions.resetSave(recordObject);
      }
    );
  };

  handleOnChangeForm = (event) => {
    let value = JavascriptUtilsTooling.getInputValueFromEvent(event);
    let oldValues = JavascriptUtilsTooling.deepCopy(this.props.save.data.values)
    let index = event.currentTarget.dataset.path;
    oldValues[index] = value;
    this.props.actions.setSaveDataByKey("values",oldValues);    
    this.setState({ formHasChanges: true });
  };
    
  handleOnSaveForm = async (event) => {
    await this.props.actions.save(this.props.save.data);
  };
    
  handleDeletePopup = async (event) => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.deleteConfirmPopup, this.state, 
      this.setState.bind(this), this.props.delete.loading, () => {
        let recordObject = JSON.parse(event.currentTarget.dataset.recorddata);
        let ids = Object.keys(recordObject.ids).map(key => recordObject.ids[key]).join(",");
        this.props.actions.prepareDelete(ids);
      }
    );
  };

  handleDeleteConfirm = async (event) => {
    await this.props.actions.deleteMetricData({
      id: this.props.delete.id,
      period_id: this.state.dataFiltersActive.periodIdSelected
    });
  };

  handleRefreshLoadDataInfoPopup = event => {
    this.props.actions.getProcessingMetricsImportsNotifications(this.state.dataFilters.periodIdSelected);
  };

  handleLoadDataInfoPopup = event => {
    InterfaceTooling.handleShowPopup(
      event, popupIds.loadDataInfoPopup, this.state, 
      this.setState.bind(this), this.props.processingMetricsImportsNotifications.loading, () => {
        this.props.actions.getProcessingMetricsImportsNotifications(this.state.dataFilters.periodIdSelected);
      }
    );
  };

  handleLoadDataInfoPopupNextPage = async (event) => {
    var page = +event.currentTarget.dataset.targetpage;
    if(this.props.processingMetricsImportsNotifications.status === 200 && 
       this.props.processingMetricsImportsNotifications.page<=this.props.processingMetricsImportsNotifications.totalPages) {
      await this.props.actions.getProcessingMetricsImportsNotifications(this.state.dataFilters.periodIdSelected, page);
    }  
  };

  handleOnToggleAuditsCheckbox = () => {
    this.setState({
      showAudits: !this.state.showAudits
    });
  };

  handleRefreshVirtualMetricData = async (event) => {
    var metricGroupSelected = (this.state.metricGroupImport && this.state.metricGroupImport!=Constants.metricGroups.CUSTOM_METRICS) ? JSON.parse(this.state.metricGroupImport) : null;
    let metricsIdSelected = metricGroupSelected ? (metricGroupSelected.metric_ids ? metricGroupSelected.metric_ids.split(",") : []) : this.state.dataFilters.metricsFilter.map( item => item.id)
    await this.props.actions.refreshVirtualMetricData({
      period_id: this.state.dataFilters.periodIdSelected,
      metrics_ids: metricsIdSelected,
      group_name: metricGroupSelected ? metricGroupSelected.name : "", 
    });
  };

  render() {
    var loadingView =  this.props.periodsList.firstLoading || ((this.props.metricsList.firstLoading || this.props.importFilesList.firstLoading ||
        this.props.metricsGroupsWithDefaults.loading || (this.props.groupsList.firstLoading || this.props.groupsList.loading)) && this.props.periodsList.status == 200 && this.props.periodsList.data.length>0);
    var errorLoadingView = (this.props.periodsList.status !== null && this.props.periodsList.status !== 200) ||
        (this.props.list.status !== null && this.props.list.status !== 200) ||
        (this.props.metricsList.status !== null && this.props.metricsList.status !== 200) ||
        (this.props.metricsGroupsWithDefaults.status !== null && this.props.metricsGroupsWithDefaults.status !== 200) ||
        (this.props.importFilesList.status !== null && this.props.importFilesList.status !== 200) ||
        (this.props.groupsList.status !== null && this.props.groupsList.status !== 200);
    var noPeriodsCreated = this.props.periodsList.status == 200 && this.props.periodsList.data.length==0;
    var metricsMetricType = Constants.metricAdmin.metricType.STANDARD;

    let anyActiveDynamicMetrics = this.state.dataFiltersActive.metricsFilter ? this.state.dataFiltersActive.metricsFilter.map(item => item.id).map(id => this.props.metricsList.data.find(item => item.id == id)).filter(item => item.metric_type == Constants.metricAdmin.metricType.DYNAMIC).length>0 : null;
    let anyDynamicMetricsInFilters = this.state.dataFilters.metricsFilter ? this.state.dataFilters.metricsFilter.map(item => item.id).map(id => this.props.metricsList.data.find(item => item.id == id)).filter(item => item.metric_type == Constants.metricAdmin.metricType.DYNAMIC).length>0 : null;
    metricsMetricType = anyDynamicMetricsInFilters ? Constants.metricAdmin.metricType.DYNAMIC : metricsMetricType;

    let anyActiveVirtualMetrics = this.state.dataFiltersActive.metricsFilter ? this.state.dataFiltersActive.metricsFilter.map(item => item.id).map(id => this.props.metricsList.data.find(item => item.id == id)).filter(item => item.metric_type == Constants.metricAdmin.metricType.VIRTUAL).length>0 : null;
    let anyVirtualMetricsInFilters = this.state.dataFilters.metricsFilter ? this.state.dataFilters.metricsFilter.map(item => item.id).map(id => this.props.metricsList.data.find(item => item.id == id)).filter(item => item.metric_type == Constants.metricAdmin.metricType.VIRTUAL).length>0 : null;
    metricsMetricType =  anyVirtualMetricsInFilters ? Constants.metricAdmin.metricType.VIRTUAL : metricsMetricType;

    let disableHeaderButtons = ((this.props.list.firstLoading || this.state.filtersChanged) && !this.props.list.loading) || 
      (this.props.list.loading && this.props.list.paginationStatus==null);
      
    return (
      <section className="dashboard_content_inner_view entity_admin_container metric_data_admin_container">
        {
          loadingView ? (<FullScreenGlassLoading/>) :
          noPeriodsCreated ? (
            <section className={"metric_data_admin_no_period_wrapper"}>
              <div className="metric_data_admin_container_data_section">
                <Header/>
                <div className="no_period_selected_wrapper">
                  {intl.formatMessage({ id: "common.noPeriodCreated" })}
                </div>
              </div>
            </section>            
          ) :
          errorLoadingView ? (<ErrorLoadingView/>) :
          (
            <section className={"left_filter_fixed_section_parent" + (this.state.filtersCollapse ? " left_filter_fixed_section_parent_collapse" : "")}>
              <div className="left_filter_fixed_section">
                <Filters
                  onChangePeriod={this.handleOnChangePeriod}
                  periodsData={this.props.periodsList.data}
                  onChangeFilter={this.handleOnChangeDataFilters}
                  filterValues={this.state.dataFilters}
                  selectedMetricGroup={this.state.metricGroupImport}
                  metricGroups={this.props.metricsGroupsWithDefaults.data}
                  handleOnChangeMetricGroup={this.handleOnChangeMetricGroup}
                  metricsDataOptions={this.props.metricsList.data}
                  calculationUnitsData={this.props.calculationUnitsFilter.data}
                  calculationUnitsLoading={this.props.calculationUnitsFilter.loading}
                  calculationUnitsFetchAction={this.handleFetchFilterCalculationUnits}
                  loadingMetricsDataButton={this.props.list.loading && this.props.list.paginationStatus==null}
                  handleLoadMetrics={this.handleLoadMetricsData}
                  disabledPeriodSelector={this.props.fixedPeriodId!=""}
                />
                <div className="left_filter_fixed_section_collapse_component" onClick={this.handleFiltersCollapse}>
                  <i className={"fas "+ (this.state.filtersCollapse ? "fa-angle-right" : "fa-angle-left")}/>
                </div>
              </div>
              <div className="left_filter_right_section metric_data_admin_container_data_section">
                <Header
                  disabledImportButton={((disableHeaderButtons && anyVirtualMetricsInFilters) || 
                    this.state.dataFilters.metricsFilter.length<=0 || anyDynamicMetricsInFilters) ? true : false}
                  disableDeleteButton={disableHeaderButtons}
                  disableExportButton={disableHeaderButtons}
                  disabledCreateButton={disableHeaderButtons || anyActiveDynamicMetrics || anyActiveVirtualMetrics}
                  hideDeleteMarksButton={!this.state.dataFilters["showMarks"] || disableHeaderButtons}
                  loadingExportButton={this.props.exportMetricsData.loading}
                  loadingRefreshVirtualMetricsButton={this.props.refreshVirtualMetricData.loading}
                  handleImportFormPopup={anyVirtualMetricsInFilters ? this.handleRefreshVirtualMetricData : this.handleShowImportFormPopup}
                  handleDeleteMetricsData={this.handleDeleteMetricsDataPopup}
                  handleExportMetricsData={this.handleExportMetricsData}
                  handleCreate={this.handleShowFormPopup}
                  handleLoadDataInfoPopup={this.handleLoadDataInfoPopup}
                  handleDeleteMarksButton={this.handleDeleteMetricsMarksDataPopup}
                  importButtonLabel={anyVirtualMetricsInFilters ? 
                    <FormattedMessage  id="metricDataAdmin.refreshVirtualMetricDataLabel"/> : 
                    <FormattedMessage  id="common.entity.importEntities"/>
                  }
                  metricsMetricType={metricsMetricType}
                  additionalData={this.props.list.additionalData}
                />
                {(!(this.props.list.firstLoading || this.state.filtersChanged) && (!this.props.list.loading || this.props.list.paginationStatus!=null)) && (
                  <Table
                    data={this.props.list.data}
                    additionalData={this.props.list.additionalData}
                    loading={this.props.list.loading}
                    page={this.props.list.page}
                    totalPages={this.props.list.totalPages}
                    totalRecords={this.props.list.totalRecords}
                    fetchListChangePage={this.fetchMetricsDataNextPage}
                    filtersText={this.state.filtersText}
                    groupsData={this.props.groupsList.data}
                    otherFilters={this.state.otherFilters}
                    handleOnChangeOtherFilters={this.handleOnChangeOtherFilters}
                    handleReloadDataWithNewFilters={this.handleReloadDataWithNewFilters}
                    handleShowFormPopup={this.handleShowFormPopup}
                    disabledEdit={anyActiveDynamicMetrics || anyActiveVirtualMetrics}
                    disabledDelete={anyActiveVirtualMetrics}
                    handleDelete={this.handleDeletePopup}
                    filtersCollapse={this.state.filtersCollapse}
                    showAuditsColumn={this.state.showAudits}
                    handleShowAuditPopup={this.props.handleNotificationDetailPopupReference}
                    handleOnToggleAuditsCheckbox={this.handleOnToggleAuditsCheckbox}
                    auditsCheckboxValue={this.state.showAudits}
                    auditDisabled={this.state.dataFiltersActive.metricsFilter.length > 1 ? true : false}
                  />
                )}
                {((this.props.list.firstLoading || this.state.filtersChanged) && !this.props.list.loading) && (
                  <div className="no_metrics_selected_wrapper info_loading_metrics_panel">
                    {intl.formatMessage({ id: "metricDataAdmin.selectMetrics" })}
                  </div>
                )}
                {(this.props.list.loading && this.props.list.paginationStatus==null) && (
                  <div className="loading_metrics_wrapper info_loading_metrics_panel">
                    <Spinner/>
                  </div>
                )}
                <ImportMetricDataFormPopup
                  handleShowPopup={this.handleShowImportFormPopup}
                  handleDownloadImportSheet={this.handleDownloadImportSheet}
                  handleButton={this.handleOnSaveImportForm}
                  loadingButton={this.props.import.loading}
                  fullLoading={this.props.import.loading || this.props.downloadImport.loading}
                  statusButton={this.props.import.status}
                  errors={this.props.import.errors}
                  popupState={this.state[popupIds.importFormPopup]}
                  importFilesList={this.props.importFilesList.data}
                  selectedImportFile={this.state.selectedImportFile}
                  handleOnChangeImportFile={this.handleOnChangeImportFile}
                  metricGroupSelected={this.state.metricGroupImport!=Constants.metricGroups.CUSTOM_METRICS ? JSON.parse(this.state.metricGroupImport) : this.state.metricGroupImport}
                  metricsString={this.state.dataFilters.metricsFilter.map(item => item.name).join(", ")}
                />
                <LoadDataInfoPopup
                  handleShowPopup={this.handleLoadDataInfoPopup}
                  handleRefresh={this.handleRefreshLoadDataInfoPopup}
                  fullLoading={this.props.processingMetricsImportsNotifications.firstLoading}
                  tableLoading={this.props.processingMetricsImportsNotifications.loading}
                  listState={this.props.processingMetricsImportsNotifications}
                  handleNextPage={this.handleLoadDataInfoPopupNextPage}
                  handleRowClick={this.props.handleNotificationDetailPopupReference}
                  popupState={this.state[popupIds.loadDataInfoPopup]}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"metricDataAdmin.deleteMetricsDataConfirmTitle"}/>)}
                  confirmText={(<FormattedMessage id={"metricDataAdmin.deleteMetricsDataConfirm"}/>)}
                  handleConfirm={this.handleDeleteMetricsDataConfirm}
                  handleCancel={this.handleDeleteMetricsDataPopup}
                  popupState={this.state[popupIds.deleteMetricsDataPopup]}
                  loading={this.props.deleteMetricsData.loading}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"metricDataAdmin.deleteMetricsMarksDataConfirmTitle"}/>)}
                  confirmText={(<FormattedMessage id={"metricDataAdmin.deleteMetricsMarksDataConfirm"}/>)}
                  handleConfirm={this.handleDeleteMetricsMarksDataConfirm}
                  handleCancel={this.handleDeleteMetricsMarksDataPopup}
                  popupState={this.state[popupIds.deleteMetricsMarksDataPopup]}
                  loading={this.props.deleteMetricsMarksData.loading}
                />
                <FormPopup
                  handleShowPopup={this.handleShowFormPopup}
                  handleOnChangeForm={this.handleOnChangeForm}
                  handleButton={this.handleOnSaveForm}
                  loadingButton={this.props.save.loading}
                  fullLoading={this.props.save.loading}
                  statusButton={this.props.save.status}
                  formData={this.props.save.data}
                  lists={this.props.list.additionalData ? this.props.list.additionalData.lists : null}
                  groupsData={this.props.groupsList.data}
                  selectedPeriodName={this.state.selectedPeriodName}
                  calculationUnitsData={this.props.calculationUnitsFilter.data}
                  calculationUnitsLoading={this.props.calculationUnitsFilter.loading}
                  calculationUnitsFetchAction={this.handleFetchFilterCalculationUnits}
                  headers={this.props.list.additionalData ? this.props.list.additionalData.headers : null}
                  errors={this.props.save.errors}
                  popupState={this.state[popupIds.formPopup]}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"metricDataAdmin.deleteConfirmTitle"}/>)}
                  confirmText={(<FormattedMessage id={"metricDataAdmin.deleteConfirm"}/>)}
                  handleConfirm={this.handleDeleteConfirm}
                  handleCancel={this.handleDeletePopup}
                  popupState={this.state[popupIds.deleteConfirmPopup]}
                  loading={this.props.delete.loading}
                />
                <GlassConfirmPopup
                  confirmTitle={(<FormattedMessage id={"common.confirmExitPopupTitle"}/>)}
                  confirmText={(<FormattedMessage id={"common.confirmExitPopupDetail"}/>)}
                  handleConfirm={InterfaceTooling.handleCloseForm.bind(null, this)}
                  handleCancel={InterfaceTooling.handleCloseFormConfirmPopup.bind(null, this)}
                  popupState={InterfaceTooling.getCloseFormConfirmPopupState(this)}
                  loading={false}
                />
              </div>
            </section>
          )
        }
      </section>
    );
  }
}

const mapStateToProps = state => ({
  list: state.reducer.metricDataAdmin.list,
  metricsList: state.reducer.metricDataAdmin.metricsList,
  metricsGroupsWithDefaults: state.reducer.metricDataAdmin.metricsGroupsWithDefaults,
  import: state.reducer.metricDataAdmin.import,
  downloadImport: state.reducer.metricDataAdmin.downloadImport,
  importFilesList: state.reducer.metricDataAdmin.importFilesList,
  calculationUnitsFilter: state.reducer.metricDataAdmin.calculationUnitsFilter,
  groupsList: state.reducer.metricDataAdmin.groupsList,
  deleteMetricsData: state.reducer.metricDataAdmin.deleteMetricsData,
  deleteMetricsMarksData: state.reducer.metricDataAdmin.deleteMetricsMarksData,
  save: state.reducer.metricDataAdmin.save,
  delete: state.reducer.metricDataAdmin.delete,
  exportMetricsData: state.reducer.metricDataAdmin.exportMetricsData,  
  processingMetricsImportsNotifications: state.reducer.metricDataAdmin.processingMetricsImportsNotifications,
  periodsList: state.reducer.metricDataAdmin.periodsList,
  periodId: state.reducer.metricDataAdmin.periodId,
  handleNotificationDetailPopupReference: state.reducer.dashboard.handleNotificationDetailPopupReference,
  fixedPeriodId: state.reducer.dashboard.fixedPeriodId,
  refreshVirtualMetricData: state.reducer.metricDataAdmin.refreshVirtualMetricData,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(metricDataAdminActions, dispatch)
});

MetricDataAdmin.propTypes = {};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MetricDataAdmin);



// WEBPACK FOOTER //
// ./source/client/containers/metricDataAdmin/index.jsx